"use strict";
import { CurrencyAmount } from "@uniswap/sdk-core";
import { useWeb3React } from "@web3-react/core";
import { NATIVE_CHAIN_ID } from "constants/tokens";
import { useCurrency } from "hooks/Tokens";
import { useAccount } from "hooks/useAccount";
import { GasSpeed, useTransactionGasFee } from "hooks/useTransactionGasFee";
import { useUSDTokenUpdater } from "hooks/useUSDTokenUpdater";
import { useCurrencyBalances } from "lib/hooks/useCurrencyBalance";
import tryParseCurrencyAmount from "lib/utils/tryParseCurrencyAmount";
import { useMemo } from "react";
import { useMultichainContext } from "state/multichain/useMultichainContext";
import { nativeOnChain } from "uniswap/src/constants/tokens";
import { useAddressFromEns, useENSName } from "uniswap/src/features/ens/api";
import { useUnitagByAddress, useUnitagByName } from "uniswap/src/features/unitags/hooks";
import { isAddress } from "utilities/src/addresses";
import { useCreateTransferTransaction } from "utils/transfer";
export var SendInputError = /* @__PURE__ */ ((SendInputError2) => {
  SendInputError2["INSUFFICIENT_FUNDS"] = "INSUFFICIENT_FUNDS";
  SendInputError2["INSUFFICIENT_FUNDS_FOR_GAS"] = "INSUFFICIENT_FUNDS_FOR_GAS";
  return SendInputError2;
})(SendInputError || {});
export function useDerivedSendInfo(state) {
  const account = useAccount();
  const { provider } = useWeb3React();
  const { chainId } = useMultichainContext();
  const { exactAmountToken, exactAmountFiat, inputInFiat, inputCurrency, recipient, validatedRecipientData } = state;
  const userInput = validatedRecipientData ? void 0 : recipient;
  const isRecipientAnAddress = isAddress(userInput ?? "");
  const reverseLookupInput = isRecipientAnAddress ? userInput : void 0;
  const { data: reverseLookupName } = useENSName(reverseLookupInput);
  const forwardLookupInput = !isRecipientAnAddress ? userInput ?? null : null;
  const { data: forwardLookupAddress } = useAddressFromEns(forwardLookupInput);
  const { unitag: recipientInputUnitag } = useUnitagByName(userInput);
  const recipientInputUnitagAddress = recipientInputUnitag?.address?.address;
  const recipientInputUnitagUsername = validatedRecipientData?.unitag ?? recipientInputUnitag?.username;
  const validatedRecipientAddress = useMemo(() => {
    return validatedRecipientData?.address ?? (isAddress(userInput) || isAddress(forwardLookupAddress) || isAddress(recipientInputUnitagAddress) || void 0);
  }, [validatedRecipientData?.address, userInput, forwardLookupAddress, recipientInputUnitagAddress]);
  const { unitag: fallbackUnitag } = useUnitagByAddress(
    recipientInputUnitagUsername ? void 0 : validatedRecipientAddress
  );
  const finalEnsName = useMemo(() => {
    if (isAddress(forwardLookupAddress)) {
      return userInput;
    }
    return validatedRecipientData?.ensName ?? reverseLookupName ?? void 0;
  }, [forwardLookupAddress, validatedRecipientData?.ensName, reverseLookupName, userInput]);
  const finalUnitag = useMemo(() => {
    if (validatedRecipientData?.unitag) {
      return validatedRecipientData.unitag;
    }
    return recipientInputUnitagUsername ?? fallbackUnitag?.username;
  }, [validatedRecipientData?.unitag, recipientInputUnitagUsername, fallbackUnitag?.username]);
  const recipientData = useMemo(() => {
    if (!validatedRecipientAddress) {
      return void 0;
    }
    return {
      address: validatedRecipientAddress,
      ensName: finalEnsName,
      unitag: finalUnitag
    };
  }, [validatedRecipientAddress, finalEnsName, finalUnitag]);
  const nativeCurrency = useCurrency({ address: NATIVE_CHAIN_ID, chainId });
  const [inputCurrencyBalance, nativeCurrencyBalance] = useCurrencyBalances(
    account.address,
    useMemo(() => [inputCurrency, nativeCurrency], [inputCurrency, nativeCurrency])
  );
  const { formattedAmount: exactAmountOut } = useUSDTokenUpdater({
    isFiatInput: inputInFiat,
    exactAmount: exactAmountToken ?? exactAmountFiat,
    exactCurrency: inputCurrency
  });
  const parsedTokenAmount = useMemo(() => {
    return tryParseCurrencyAmount(inputInFiat ? exactAmountOut : exactAmountToken, inputCurrency);
  }, [exactAmountOut, exactAmountToken, inputCurrency, inputInFiat]);
  const transferInfo = useMemo(() => {
    return {
      provider,
      account: account.address,
      chainId,
      currencyAmount: parsedTokenAmount,
      toAddress: recipientData?.address
    };
  }, [account.address, chainId, parsedTokenAmount, provider, recipientData?.address]);
  const transferTransaction = useCreateTransferTransaction(transferInfo);
  const gasFee = useTransactionGasFee(transferTransaction ?? void 0, GasSpeed.Normal);
  const gasFeeCurrencyAmount = useMemo(() => {
    if (!chainId || !gasFee.value) {
      return void 0;
    }
    return CurrencyAmount.fromRawAmount(nativeOnChain(chainId), gasFee.value);
  }, [chainId, gasFee.value]);
  const inputError = useMemo(() => {
    const insufficientBalance = parsedTokenAmount && inputCurrencyBalance?.lessThan(parsedTokenAmount);
    if (insufficientBalance) {
      return "INSUFFICIENT_FUNDS" /* INSUFFICIENT_FUNDS */;
    }
    if (!gasFee.value || !nativeCurrency || !nativeCurrencyBalance) {
      return void 0;
    }
    let totalAmount = CurrencyAmount.fromRawAmount(nativeCurrency, gasFee.value);
    if (parsedTokenAmount && inputCurrency && nativeCurrency.equals(inputCurrency)) {
      totalAmount = totalAmount.add(parsedTokenAmount);
    }
    if (!totalAmount || nativeCurrencyBalance.lessThan(totalAmount)) {
      return "INSUFFICIENT_FUNDS_FOR_GAS" /* INSUFFICIENT_FUNDS_FOR_GAS */;
    }
    return void 0;
  }, [gasFee.value, inputCurrency, inputCurrencyBalance, nativeCurrencyBalance, nativeCurrency, parsedTokenAmount]);
  return useMemo(
    () => ({
      currencyBalance: inputCurrencyBalance,
      exactAmountOut,
      parsedTokenAmount,
      recipientData,
      transaction: transferTransaction ?? void 0,
      gasFeeCurrencyAmount,
      gasFee,
      inputError
    }),
    [
      exactAmountOut,
      gasFeeCurrencyAmount,
      gasFee,
      inputCurrencyBalance,
      inputError,
      parsedTokenAmount,
      recipientData,
      transferTransaction
    ]
  );
}
